const VERTEX_TAX_AMOUNT_PATTERN = /VertexTaxAmount::([0-9]*\.?[0-9]+)/i;

const PROVINCIAL_TAX_ABBREVIATIONS: Array<[string, string]> = [
  ['provincial', 'PST'],
  ['quebec', 'QST'],
];

/**
 * Normalizes a Canadian tax sub-rate name for display, e.g.
 * "Provincial Sales Tax (VAT) - VertexTaxAmount::1.25" -> "PST".
 */
export const getCaTaxName = (name?: string): string => {
  if (!name) return '';

  const taxType = name.split(' - ')[0];
  const abbreviation = PROVINCIAL_TAX_ABBREVIATIONS.find(([province]) => taxType.toLowerCase().includes(province));

  if (abbreviation) return abbreviation[1];

  return taxType.replace(/\s*\(VAT\)\s*/gi, '').trim();
};

/**
 * Lookup key used to pair a line item sub-rate with its shipping counterpart.
 * GST variants (e.g. "GST/HST") collapse to a single "gst" key.
 */
export const getCaTaxKey = (taxName: string): string =>
  taxName.toLowerCase().includes('gst') ? 'gst' : taxName.toLowerCase();

/**
 * Reads the Vertex tax amount encoded in a sub-rate name. Returns 0 when absent or unparseable.
 */
export const getVertexTaxAmount = (name?: string): number => {
  const vertexTaxAmountMatch = name?.match(VERTEX_TAX_AMOUNT_PATTERN);
  const vertexTaxAmount = Number.parseFloat(vertexTaxAmountMatch?.[1] ?? '0');

  return Number.isNaN(vertexTaxAmount) ? 0 : vertexTaxAmount;
};

/**
 * Vertex tax amount of the shipping sub-rate matching the given tax key.
 */
export const getShippingVertexTaxAmount = (shippingSubRates: any[], taxKey: string): number => {
  const matchingShippingSubRate = shippingSubRates?.find(
    (shippingSubRate: any) => getCaTaxKey(getCaTaxName(shippingSubRate?.name)) === taxKey,
  );

  return getVertexTaxAmount(matchingShippingSubRate?.name);
};

/**
 * Display rows for Canadian tax: each sub-rate's Vertex amount summed across every
 * line item, plus the Vertex amount of the shipping sub-rate for the same tax.
 */
export const getCaSubRateTaxes = (lineItems: any[] = [], shippingSubRates: any[] = []) => {
  const rowsByTaxKey = new Map<string, { taxName: string; taxPercent: number; totalTaxAmount: number }>();

  lineItems?.forEach((lineItem: any) =>
    lineItem?.taxRate?.subRates?.forEach((subRate: any) => {
      const taxName = getCaTaxName(subRate?.name);
      const taxKey = getCaTaxKey(taxName);
      const existingRow = rowsByTaxKey.get(taxKey);

      if (existingRow) {
        existingRow.totalTaxAmount += getVertexTaxAmount(subRate?.name);
        return;
      }

      rowsByTaxKey.set(taxKey, {
        taxName,
        taxPercent: Number(subRate?.amount ?? 0) * 100,
        totalTaxAmount: getVertexTaxAmount(subRate?.name) + getShippingVertexTaxAmount(shippingSubRates, taxKey),
      });
    }),
  );

  return [...rowsByTaxKey.values()];
};
